/**
 * Lunarness required harness sizing.
 * Owns selection state, fit recommendation, accessible modal behaviour and
 * the product-form purchase gate. No size is preselected by design.
 */
(function () {
  'use strict';

  if (customElements.get('product-sizing')) return;

  class ProductSizing extends HTMLElement {
    constructor() {
      super();
      this.mode = 'standard';
      this.unit = this.dataset.initialUnit === 'inch' ? 'inch' : 'cm';
      this.form = null;
      this.addButton = null;
      this.variantAvailable = true;
      this.proxyForms = [];
      this.proxyObservers = [];
      this.lastFocusedElement = null;
      this.boundFormSubmit = this.onFormSubmit.bind(this);
      this.boundFormClick = this.onFormClick.bind(this);
      this.boundVariantChange = this.onVariantChange.bind(this);
      this.boundKeydown = this.onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cacheElements();
      this.bindEvents();
      this.setUnit(this.unit, false);
      this.setMode('standard', false);
      this.connectForm();
      this.renderRanges();
      this.syncState();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.boundFormSubmit, true);
      this.form?.removeEventListener('click', this.boundFormClick, true);
      this.formObserver?.disconnect();
      this.proxyForms.forEach((form) => {
        form.removeEventListener('submit', this.boundFormSubmit, true);
        form.removeEventListener('click', this.boundFormClick, true);
      });
      this.proxyObservers.forEach((observer) => observer.disconnect());
      this.proxyForms.forEach((form) => this.syncProxyForm(form, false));
      document.removeEventListener('variant:changed', this.boundVariantChange);
      document.removeEventListener('keydown', this.boundKeydown);
      document.documentElement.classList.remove('ps-modal-open');
      document.body.classList.remove('ps-modal-open');
    }

    cacheElements() {
      this.modeButtons = [...this.querySelectorAll('[data-mode]')];
      this.unitButtons = [...this.querySelectorAll('[data-unit]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.sizeRadios = [...this.querySelectorAll('[data-size-radio]')];
      this.customSize = this.querySelector('[data-custom-size]');
      this.customFields = [...this.querySelectorAll('[data-custom-field]')];
      this.fitType = this.querySelector('[data-fit-type]');
      this.measurementUnit = this.querySelector('[data-measurement-unit]');
      this.status = this.querySelector('[data-sizing-status]');
      this.statusText = this.querySelector('[data-sizing-status-text]');
      this.finder = this.querySelector('[data-fit-finder]');
      this.finderToggle = this.querySelector('[data-toggle-fit-finder]');
      this.fitInputs = [...this.querySelectorAll('[data-fit-input]')];
      this.recommendation = this.querySelector('[data-recommendation]');
      this.modal = this.querySelector('[data-size-guide-modal]');
      this.dialog = this.modal?.querySelector('.ps__dialog');
      this.rangeCells = [...this.querySelectorAll('[data-range-cell]')];
      this.fitRows = [...this.querySelectorAll('[data-fit-row]')];
      this.sizeCards = [...this.querySelectorAll('[data-size-card]')];
    }

    bindEvents() {
      this.modeButtons.forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.mode)));
      this.unitButtons.forEach((button) => button.addEventListener('click', () => this.setUnit(button.dataset.unit)));
      this.sizeRadios.forEach((radio) => radio.addEventListener('change', () => this.onSizeChange(radio.value)));
      this.customFields.forEach((field) => {
        field.addEventListener('input', () => this.syncState());
        field.addEventListener('blur', () => this.validateCustomField(field));
      });
      this.finderToggle?.addEventListener('click', () => this.toggleFinder());
      this.querySelector('[data-recommend-size]')?.addEventListener('click', () => this.recommendSize());
      this.querySelectorAll('[data-open-size-guide]').forEach((button) => button.addEventListener('click', () => this.openModal(button)));
      this.querySelectorAll('[data-close-size-guide]').forEach((button) => button.addEventListener('click', () => this.closeModal()));
      document.addEventListener('variant:changed', this.boundVariantChange);
      document.addEventListener('keydown', this.boundKeydown);
    }

    connectForm(attempt = 0) {
      const formId = this.dataset.formId;
      const form = formId ? document.getElementById(formId) : null;
      if (!form) {
        if (attempt < 20) window.setTimeout(() => this.connectForm(attempt + 1), 50);
        return;
      }

      if (this.form === form) return;
      this.form = form;
      this.addButton = form.querySelector('[name="add"]');
      this.variantAvailable = form.dataset.variantAvailable !== 'false';
      form.addEventListener('submit', this.boundFormSubmit, true);
      form.addEventListener('click', this.boundFormClick, true);
      this.formObserver?.disconnect();
      this.formObserver = new MutationObserver(() => this.syncPurchaseControls());
      this.formObserver.observe(form, { childList: true, subtree: true });
      this.connectProxyForms();
      this.syncPurchaseControls();
    }

    connectProxyForms() {
      const productId = String(this.dataset.productId || '');
      if (!productId) return;
      const selector = `form[data-sizing-proxy-product-id="${CSS.escape(productId)}"]`;
      this.proxyForms = [...document.querySelectorAll(selector)].filter((form) => form !== this.form);
      this.proxyForms.forEach((form) => {
        form.addEventListener('submit', this.boundFormSubmit, true);
        form.addEventListener('click', this.boundFormClick, true);
        const observer = new MutationObserver((mutations) => {
          const onlyProxyPropertiesChanged = mutations.every((mutation) =>
            [...mutation.addedNodes, ...mutation.removedNodes].every((node) =>
              node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('data-sizing-proxy-property')
            )
          );
          if (!onlyProxyPropertiesChanged) this.syncProxyForm(form, this.isValid());
        });
        observer.observe(form, { childList: true, subtree: true });
        this.proxyObservers.push(observer);
      });
    }

    setMode(mode, announce = true) {
      if (mode !== 'standard' && mode !== 'custom') return;
      this.mode = mode;
      const custom = mode === 'custom';

      this.modeButtons.forEach((button) => {
        const active = button.dataset.mode === mode;
        button.classList.toggle('ps__mode-btn--active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.panel === mode;
        panel.classList.toggle('ps__panel--active', active);
        panel.hidden = !active;
      });

      this.sizeRadios.forEach((radio) => {
        radio.disabled = custom;
        if (custom) radio.checked = false;
      });
      if (this.customSize) this.customSize.disabled = !custom;
      this.customFields.forEach((field) => {
        field.disabled = !custom;
        if (!custom) field.removeAttribute('aria-invalid');
      });
      if (this.fitType) this.fitType.value = custom ? 'Custom fit' : 'Standard';

      this.clearHighlights();
      this.syncState();
      if (announce && custom) this.setStatus('Enter every required measurement to continue.', 'pending');
    }

    setUnit(nextUnit, convertValues = true) {
      if (nextUnit !== 'inch' && nextUnit !== 'cm') return;
      const previousUnit = this.unit;
      this.unit = nextUnit;

      if (convertValues && previousUnit !== nextUnit) {
        [...this.fitInputs, ...this.customFields.filter((field) => field.tagName === 'INPUT')].forEach((field) => {
          const value = Number.parseFloat(field.value);
          if (!Number.isFinite(value)) return;
          const converted = nextUnit === 'cm' ? value * 2.54 : value / 2.54;
          field.value = String(Math.round(converted * 10) / 10);
        });
      }

      this.unitButtons.forEach((button) => {
        const active = button.dataset.unit === nextUnit;
        button.setAttribute('aria-pressed', String(active));
      });
      this.querySelectorAll('[data-unit-short]').forEach((node) => { node.textContent = nextUnit === 'inch' ? 'in' : 'cm'; });
      this.querySelectorAll('[data-unit-badge]').forEach((node) => { node.textContent = nextUnit === 'inch' ? 'US · IN' : 'EU · CM'; });
      if (this.measurementUnit) this.measurementUnit.value = nextUnit === 'inch' ? 'inches' : 'centimeters';

      this.customFields.filter((field) => field.tagName === 'INPUT').forEach((field) => {
        const cmMin = Number.parseFloat(field.dataset.cmMin) || 10;
        const cmMax = Number.parseFloat(field.dataset.cmMax) || 250;
        const min = nextUnit === 'inch' ? cmMin / 2.54 : cmMin;
        const max = nextUnit === 'inch' ? cmMax / 2.54 : cmMax;
        field.min = String(Math.round(min * 10) / 10);
        field.max = String(Math.round(max * 10) / 10);
      });

      this.renderRanges();
      this.syncState();
    }

    renderRanges() {
      const suffix = this.unit === 'inch' ? 'in' : 'cm';
      this.rangeCells.forEach((cell) => {
        const min = cell.dataset[`${this.unit}Min`];
        const max = cell.dataset[`${this.unit}Max`];
        if (min && max) cell.textContent = `${min}–${max} ${suffix}`;
      });
    }

    toggleFinder() {
      if (!this.finder) return;
      const willOpen = this.finder.hidden;
      this.finder.hidden = !willOpen;
      this.finderToggle?.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) this.fitInputs[0]?.focus({ preventScroll: true });
    }

    recommendSize() {
      const measurements = this.fitInputs
        .map((input) => ({ type: input.dataset.fitInput, value: Number.parseFloat(input.value), input }))
        .filter((item) => Number.isFinite(item.value) && item.value > 0);

      if (!measurements.length) {
        this.showRecommendation('Enter at least one measurement to get a suggestion.', false);
        this.fitInputs[0]?.focus();
        return;
      }

      let targetIndex = 0;
      for (const measurement of measurements) {
        let measurementIndex = -1;
        for (let index = 0; index < this.fitRows.length; index += 1) {
          const cell = this.fitRows[index].querySelector(`[data-measure="${measurement.type}"]`);
          const max = Number.parseFloat(cell?.dataset[`${this.unit}Max`]);
          if (Number.isFinite(max) && measurement.value <= max) {
            measurementIndex = index;
            break;
          }
        }
        if (measurementIndex === -1) {
          this.showRecommendation('Your measurement is outside our standard chart. Choose Custom fit for the best result.', false);
          this.setMode('custom');
          return;
        }
        targetIndex = Math.max(targetIndex, measurementIndex);
      }

      const size = this.fitRows[targetIndex]?.dataset.size;
      if (!size) return;
      this.selectSize(size);
      this.showRecommendation(`<strong>${size} is your suggested size.</strong> We selected it above. If you prefer a looser fit or your measurements span several sizes, choose Custom fit.`, true);
    }

    showRecommendation(message, trustedHtml) {
      if (!this.recommendation) return;
      if (trustedHtml) this.recommendation.innerHTML = message;
      else this.recommendation.textContent = message;
      this.recommendation.classList.add('ps__recommendation--visible');
    }

    selectSize(size) {
      if (this.mode !== 'standard') this.setMode('standard', false);
      const radio = this.sizeRadios.find((item) => item.value === size);
      if (!radio) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onSizeChange(size) {
      this.clearHighlights();
      this.fitRows.find((row) => row.dataset.size === size)?.classList.add('ps__chart-row--selected');
      this.sizeCards.find((card) => card.dataset.sizeCard === size)?.classList.add('ps__size-card--selected');
      this.syncState();
    }

    clearHighlights() {
      this.fitRows.forEach((row) => row.classList.remove('ps__chart-row--selected'));
      this.sizeCards.forEach((card) => card.classList.remove('ps__size-card--selected'));
    }

    validateCustomField(field) {
      if (field.disabled || !field.hasAttribute('data-custom-required')) return true;
      const valid = this.customFieldIsValid(field);
      field.setAttribute('aria-invalid', String(!valid));
      return valid;
    }

    customFieldIsValid(field) {
      const value = Number.parseFloat(field.value);
      const min = Number.parseFloat(field.min);
      const max = Number.parseFloat(field.max);
      return Number.isFinite(value) && value >= min && value <= max;
    }

    isValid() {
      if (this.mode === 'standard') return this.sizeRadios.some((radio) => !radio.disabled && radio.checked);
      const required = this.customFields.filter((field) => !field.disabled && field.hasAttribute('data-custom-required'));
      return required.length > 0 && required.every((field) => this.customFieldIsValid(field));
    }

    validate(options = {}) {
      const valid = this.isValid();
      if (valid) return true;

      if (this.mode === 'standard') {
        this.setStatus('Choose a size before adding this harness to your bag.', 'error');
        if (options.focus) this.sizeRadios[0]?.focus();
      } else {
        this.setStatus('Complete all required custom measurements with a valid number.', 'error');
        const firstInvalid = this.customFields.find((field) => !field.disabled && field.hasAttribute('data-custom-required') && !this.validateCustomField(field));
        if (options.focus) firstInvalid?.focus();
      }
      return false;
    }

    syncState() {
      const valid = this.isValid();
      if (valid) {
        const selected = this.mode === 'standard' ? this.sizeRadios.find((radio) => radio.checked)?.value : 'Custom fit';
        this.setStatus(`${selected} selected. Ready to add to your bag.`, 'ready');
      } else if (!this.status?.classList.contains('ps__status--error')) {
        this.setStatus(this.mode === 'standard' ? 'Select a size to continue.' : 'Enter every required measurement to continue.', 'pending');
      }
      this.syncPurchaseControls();
    }

    setStatus(message, state) {
      if (!this.status || !this.statusText) return;
      this.statusText.textContent = message;
      this.status.classList.toggle('ps__status--ready', state === 'ready');
      this.status.classList.toggle('ps__status--error', state === 'error');
    }

    syncPurchaseControls() {
      if (!this.form) return;
      const valid = this.isValid();
      const canPurchase = valid && this.variantAvailable;
      this.form.classList.toggle('ps-form-sizing-locked', !valid);
      this.addButton = this.form.querySelector('[name="add"]');
      if (this.addButton) {
        if (!valid || !this.variantAvailable) {
          this.addButton.disabled = true;
          if (!valid) this.addButton.dataset.sizingLocked = 'true';
          if (!this.variantAvailable) this.addButton.dataset.variantLocked = 'true';
          this.addButton.setAttribute('aria-disabled', 'true');
        } else if (this.addButton.dataset.sizingLocked === 'true' || this.addButton.dataset.variantLocked === 'true') {
          this.addButton.disabled = false;
          delete this.addButton.dataset.sizingLocked;
          delete this.addButton.dataset.variantLocked;
          this.addButton.removeAttribute('aria-disabled');
        }
      }

      const checkout = this.form.querySelector('.m-product-dynamic-checkout, .shopify-payment-button');
      checkout?.classList.toggle('ps-checkout-locked', !canPurchase);
      checkout?.setAttribute('aria-disabled', String(!canPurchase));
      if (checkout) {
        checkout.inert = !canPurchase;
        checkout.toggleAttribute('data-sizing-checkout-locked', !canPurchase);
      }
      this.form.querySelectorAll('.shopify-payment-button button').forEach((button) => { button.disabled = !canPurchase; });
      this.proxyForms.forEach((form) => this.syncProxyForm(form, valid));
    }

    syncProxyForm(form, valid) {
      const canPurchase = valid && this.variantAvailable;
      form.classList.toggle('ps-form-sizing-locked', !valid);
      form.dataset.sizingBlocked = String(!valid);

      form.querySelectorAll('[data-sizing-proxy-property]').forEach((field) => field.remove());
      if (valid) {
        this.querySelectorAll('[name^="properties["]').forEach((source) => {
          if (source.disabled || !source.value) return;
          if ((source.type === 'radio' || source.type === 'checkbox') && !source.checked) return;
          const field = document.createElement('input');
          field.type = 'hidden';
          field.name = source.name;
          field.value = source.value;
          field.dataset.sizingProxyProperty = 'true';
          form.append(field);
        });
      }

      const addButton = form.querySelector('[name="add"]');
      if (addButton) {
        if (!valid || !this.variantAvailable) {
          addButton.disabled = true;
          if (!valid) addButton.dataset.sizingLocked = 'true';
          if (!this.variantAvailable) addButton.dataset.variantLocked = 'true';
          addButton.setAttribute('aria-disabled', 'true');
        } else if (addButton.dataset.sizingLocked === 'true' || addButton.dataset.variantLocked === 'true') {
          addButton.disabled = false;
          delete addButton.dataset.sizingLocked;
          delete addButton.dataset.variantLocked;
          addButton.removeAttribute('aria-disabled');
        }
      }

      const checkout = form.querySelector('.m-product-dynamic-checkout, .shopify-payment-button');
      checkout?.classList.toggle('ps-checkout-locked', !canPurchase);
      checkout?.setAttribute('aria-disabled', String(!canPurchase));
      if (checkout) {
        checkout.inert = !canPurchase;
        checkout.toggleAttribute('data-sizing-checkout-locked', !canPurchase);
      }
      form.querySelectorAll('.shopify-payment-button button').forEach((button) => { button.disabled = !canPurchase; });
    }

    onFormSubmit(event) {
      if (this.validate({ focus: true })) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    onFormClick(event) {
      const checkoutTrigger = event.target.closest('.shopify-payment-button, .shopify-payment-button__button, [data-testid*="Checkout"]');
      if (!checkoutTrigger || this.validate({ focus: true })) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    onVariantChange(event) {
      const sectionId = String(event.detail?.sectionId || '');
      if (sectionId && sectionId !== String(this.dataset.sectionId)) return;
      this.variantAvailable = Boolean(event.detail?.variant?.available);
      if (this.form) this.form.dataset.variantAvailable = String(this.variantAvailable);
      this.syncPurchaseControls();
    }

    openModal(trigger) {
      if (!this.modal || !this.dialog) return;
      this.lastFocusedElement = trigger || document.activeElement;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ps-modal-open');
      document.body.classList.add('ps-modal-open');
      window.requestAnimationFrame(() => this.dialog.focus());
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ps-modal-open');
      document.body.classList.remove('ps-modal-open');
      this.lastFocusedElement?.focus?.();
    }

    onDocumentKeydown(event) {
      if (!this.modal || this.modal.getAttribute('aria-hidden') === 'true') return;
      if (event.key === 'Escape') {
        this.closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.modal.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('product-sizing', ProductSizing);

  // Cart and drawer defense-in-depth. Liquid disables checkout and hides
  // accelerated buttons; this capture guard also blocks synthetic submits
  // produced by apps while an unsized harness remains in the cart.
  document.addEventListener('submit', (event) => {
    const blockedCart = event.target.closest('[data-sizing-blocked="true"]');
    const isCheckoutSubmit = event.submitter?.name === 'checkout' || event.submitter?.hasAttribute('data-sizing-checkout-locked');
    if (!blockedCart || !isCheckoutSubmit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    blockedCart.querySelector('.ps-cart-gate')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, true);

  document.addEventListener('click', (event) => {
    const fixSizingLink = event.target.closest('[data-fix-sizing]');
    if (fixSizingLink) {
      event.preventDefault();
      const destination = fixSizingLink.href;
      const lineKey = fixSizingLink.dataset.lineKey;
      if (!lineKey) {
        window.location.assign(destination);
        return;
      }
      fixSizingLink.setAttribute('aria-busy', 'true');
      fixSizingLink.style.pointerEvents = 'none';
      const changeUrl = window.MinimogSettings?.routes?.cart_change_url || `${window.Shopify?.routes?.root || '/'}cart/change.js`;
      fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to remove unsized item');
          window.location.assign(destination);
        })
        .catch(() => {
          fixSizingLink.removeAttribute('aria-busy');
          fixSizingLink.style.pointerEvents = '';
          fixSizingLink.textContent = 'Please remove this item, then choose a size.';
        });
      return;
    }

    const lockedCheckout = event.target.closest('[data-sizing-checkout-locked]');
    if (!lockedCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    lockedCheckout.closest('[data-sizing-blocked="true"]')?.querySelector('.ps-cart-gate')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, true);
})();
